import { useEffect, useState } from 'react';

import { Navigate, useSearchParams, useParams } from 'react-router-dom';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import useEnv from '@zenbusiness/application-commons-env';
import PageLoading from '@zenbusiness/zui/PageLoading';

import { AUTH_PATHS } from '#client/build-env';
import { validateAndSanitizeRedirectUrl } from '#client/utils';

const Callback = () => {
  const { DEFAULT_ROUTE } = useEnv();
  const { authenticated, callback } = useAuthentication();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const [loading, setLoading] = useState(true);

  // loginWithPassword encodes redirectTo as base64 in path: /dashboard/callback/L2d1aWRlL2NoYXQ=

  // Decode the path parameter (if present)
  const pathParam = params['*']; // Catches the path after /callback/

  // Decode URL query param (if present)
  const urlRedirectTo = searchParams.get('redirectTo');

  // Validate and sanitize redirects using centralized utility
  // Priority: Path param > URL param > DEFAULT_ROUTE
  const validatedPathRedirect = pathParam ? validateAndSanitizeRedirectUrl(pathParam, null) : null;
  const validatedUrlRedirect = urlRedirectTo
    ? validateAndSanitizeRedirectUrl(urlRedirectTo, null)
    : null;
  const initialRedirect = validatedPathRedirect || validatedUrlRedirect || DEFAULT_ROUTE;

  const [redirectTo, setRedirectTo] = useState(initialRedirect);
  const [exception, setException] = useState(null);

  useEffect(() => {
    if (!loading) return;
    if (authenticated) {
      setLoading(false);
      return;
    }

    callback().then((auth) => {
      const { redirectTo: url } = auth;

      // Validate Auth0 redirectTo if present
      const validatedAuthRedirect = url ? validateAndSanitizeRedirectUrl(url, null) : null;

      // Priority: Auth0 state > Validated path param > Validated URL param > default
      if (validatedAuthRedirect) {
        setRedirectTo(validatedAuthRedirect);
      } else if (validatedPathRedirect) {
        setRedirectTo(validatedPathRedirect);
      } else if (validatedUrlRedirect) {
        setRedirectTo(validatedUrlRedirect);
      }
      setLoading(false);
    }, setException);
  }, [authenticated, loading, callback, validatedPathRedirect, validatedUrlRedirect]);

  const showLoading = loading || !authenticated;

  if (exception && exception.error === 'invalid_token') {
    return <Navigate to={AUTH_PATHS.loginUri} />;
  }

  if (exception) throw exception;

  if (showLoading) {
    // Wait until callback processed, then redirect to base page.
    return <PageLoading />;
  }

  return <Navigate to={redirectTo} />;
};

export default Callback;
